import { useState, type ReactNode } from 'react'
import { type GridRenderCellParams } from '@mui/x-data-grid'
import { useLocation, useNavigate } from 'react-router-dom'
import day from 'dayjs'
import { useTranslation } from 'react-i18next'
import { type RoleNameDto } from '@dhl/mdp-api'
import { type GridColDef, Pagination } from '@dhl/components'
import { Box, Card } from '@dhl-ui/components'
import { links } from '~/AppRoutes'
import { RoleTags } from '~/modules/users/components'
import { type User } from '~/modules/users/model'
import { AdminDataGrid, sortArrayByPreference } from '~/common'

type Props = {
  organisationId: string
  isLoading: boolean
  users: User[]
  action?: ReactNode
}

export const UsersCard = ({ organisationId, isLoading, users, action }: Props) => {
  const { t } = useTranslation()
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const navigate = useNavigate()
  const location = useLocation()

  const renderCellEmail = (row: User) => row?.email
  const renderCellName = (row: User) => `${row?.name?.firstName} ${row?.name?.lastName}`
  const renderCellLogin = (row: User) => day(row?.timeLastLogin).format('DD-MM-YYYY')
  const renderCellCreated = (row: User) => day(row?.timeRegistered).format('DD-MM-YYYY')

  const orderRoles: RoleNameDto[] = ['ApiGatewayUser', 'OrganisationUserManager']

  const renderRoles = ({ value = [] }: GridRenderCellParams<User, RoleNameDto[]>) => (
    <RoleTags roles={sortArrayByPreference<RoleNameDto>(value, orderRoles)} />
  )

  const columns: GridColDef<User>[] = [
    {
      field: 'id',
    },
    {
      field: 'userType',
    },
    {
      field: 'email',
      headerName: t('admin.organisationsPage.users.emailAddress'),
      flex: 1,
      valueGetter: (value, row) => renderCellEmail(row),
      minWidth: 250,
      sortable: true,
    },
    {
      field: 'roles',
      headerName: t('admin.usersPage.userHeader.roles'),
      minWidth: 200,
      renderCell: renderRoles,
    },
    {
      field: 'name',
      headerName: t('admin.organisationsPage.users.name'),
      flex: 1,
      valueGetter: (value, row) => renderCellName(row),
      minWidth: 200,
      sortable: true,
    },
    {
      field: 'createdOn',
      headerName: t('admin.organisationsPage.users.createdOn'),
      flex: 1,
      valueGetter: (value, row) => renderCellCreated(row),
      minWidth: 100,
    },
    {
      field: 'lastLogin',
      headerName: t('admin.organisationsPage.users.lastLogin'),
      flex: 1,
      valueGetter: (value, row) => renderCellLogin(row),
      minWidth: 100,
    },
  ]

  const handleRowClick = (user: User) => {
    navigate(links.userDetailLink(user.id), { state: { from: location.pathname, id: organisationId } })
  }

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.organisationsPage.users.title')}
          endNode={action as React.ReactElement | undefined}
        />
        <Card.Content>
          <AdminDataGrid
            initialState={{
              columns: { columnVisibilityModel: { id: false, userType: false } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            }}
            onSortModelChange={() => {
              setPage(0)
            }}
            loading={isLoading}
            data-testid="users-table"
            autoHeight
            rows={users}
            columns={columns}
            paginationModel={{
              page,
              pageSize,
            }}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
            onRowClick={({ row }) => handleRowClick(row)}
          />
          <Box px={2} py={1}>
            <Pagination
              page={page}
              count={users.length}
              onPageChange={(_, page) => setPage(page)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </Card.Content>
      </Card>
    </>
  )
}
